import { useState, useEffect } from 'react'
import { 
  Card, Table, Button, Input, Select, Tag, Space, Modal, Dropdown,
  message, Tooltip, Typography 
} from 'antd'
import {
  DatabaseOutlined, SearchOutlined, EyeOutlined, 
  DeleteOutlined, DownloadOutlined, PlusOutlined,
  FilterOutlined, FileTextOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import SchemaViewer from '../../components/Training/SchemaViewer'
import SchemaEditor from '../../components/Training/SchemaEditor'

const { Title, Text } = Typography
const { Search } = Input
const { Option } = Select

interface Dataset {
  id: string
  name: string
  description?: string
  project_name?: string
  data_source_name?: string
  database_name?: string
  total_tables: number
  total_columns: number
  total_examples: number
  status: string
  source_type: string
  created_at: string
  updated_at: string
}

const DatasetListPage = () => {
  const navigate = useNavigate()
  const [datasets, setDatasets] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  
  // Schema viewer/editor state
  const [schemaViewerVisible, setSchemaViewerVisible] = useState(false)
  const [schemaEditorVisible, setSchemaEditorVisible] = useState(false)
  const [selectedDataset, setSelectedDataset] = useState<any>(null)
  const [schemaToEdit, setSchemaToEdit] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterSource, setFilterSource] = useState<string>('all')

  useEffect(() => {
    loadDatasets()
  }, [searchTerm, filterStatus, filterSource])

  const loadDatasets = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (searchTerm) params.append('search', searchTerm)
      if (filterStatus !== 'all') params.append('status', filterStatus)
      if (filterSource !== 'all') params.append('source_type', filterSource)

      const response = await axios.get(`http://localhost:5000/api/schema/datasets?${params}`)
      setDatasets(response.data.datasets)
    } catch (error) {
      message.error('Failed to load datasets')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    Modal.confirm({
      title: 'Delete Dataset',
      content: `Are you sure you want to delete "${name}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await axios.delete(`http://localhost:5000/api/schema/datasets/${id}`)
          message.success('Dataset deleted successfully')
          loadDatasets()
        } catch (error) {
          message.error('Failed to delete dataset')
        }
      },
    })
  }

  const handleDownload = async (id: string, _name: string) => {
    try {
      const response = await axios.get(`http://localhost:5000/api/schema/datasets/${id}/download`)
      window.open(response.data.download_url, '_blank')
    } catch (error) {
      message.error('Failed to generate download link')
    }
  }

  const handleViewSchema = (dataset: Dataset) => {
    setSelectedDataset(dataset)
    setSchemaViewerVisible(true)
  }

  const handleEditSchema = (schema: string) => {
    setSchemaToEdit(schema)
    setSchemaEditorVisible(true)
  }

  const handleEditorClose = (refreshNeeded: boolean) => {
    setSchemaEditorVisible(false)
    if (refreshNeeded) {
      loadDatasets() // Refresh the dataset list
    }
  }

  const handleDownloadSchema = async (dataset: Dataset) => {
    try {
      const response = await axios.get(
        `http://localhost:5000/api/schema/datasets/${dataset.id}/yaml`
      )
      
      const blob = new Blob([response.data.content], { type: 'text/yaml' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${dataset.name.replace(/\s+/g, '_')}_schema.yaml`
      link.click()
      URL.revokeObjectURL(url)
      
      message.success('Schema downloaded!')
    } catch (error) {
      message.error('Failed to download schema')
    }
  }

  const handleCompleteDataset = async (dataset: Dataset) => {
    Modal.confirm({
      title: 'Complete Dataset?',
      content: `This will generate and upload the training JSONL file for "${dataset.name}". Continue?`,
      onOk: async () => {
        try {
          message.loading({ content: 'Completing dataset...', key: 'complete', duration: 0 })
          
          const response = await axios.post(
            `http://localhost:5000/api/schema/datasets/${dataset.id}/complete`
          )
          
          message.success({ 
            content: `Dataset completed! ${response.data.total_examples} examples generated.`, 
            key: 'complete' 
          })
          
          loadDatasets()
        } catch (error: any) {
          message.error({ 
            content: error.response?.data?.detail || 'Failed to complete dataset', 
            key: 'complete' 
          })
        }
      }
    })
  }

  const columns = [
    {
      title: 'Dataset Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Dataset) => (
        <div>
          <Text strong style={{ fontSize: '14px' }}>{text}</Text>
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.description.substring(0, 100)}
                {record.description.length > 100 && '...'}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Source',
      key: 'source',
      render: (record: Dataset) => (
        <div>
          {record.source_type === 'schema' ? (
            <>
              <Tag color="blue">Database Schema</Tag>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                <Text type="secondary">{record.database_name}</Text>
              </div>
            </>
          ) : (
            <Tag color="green">Uploaded Schema</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Statistics',
      key: 'stats',
      render: (record: Dataset) => (
        <Space direction="vertical" size="small">
          <Text style={{ fontSize: '12px' }}>
            <strong>{record.total_tables}</strong> tables
          </Text>
          <Text style={{ fontSize: '12px' }}>
            <strong>{record.total_columns}</strong> columns
          </Text>
          <Text style={{ fontSize: '12px' }}>
            <strong>{record.total_examples.toLocaleString()}</strong> examples
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: Record<string, string> = {
          draft: 'orange',
          confirmed: 'green',
          training: 'blue',
          error: 'red',
        }
        return (
          <Tag color={colors[status] || 'default'}>
            {status.toUpperCase()}
          </Tag>
        )
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Dataset) => (
        <Space size="small">
          <Tooltip title="View & Search">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate(`/training/datasets/${record.id}/search`)}
            />
          </Tooltip>
          
          <Tooltip title="View Schema">
            <Button
              icon={<FileTextOutlined />}
              size="small"
              onClick={() => handleViewSchema(record)}
            />
          </Tooltip>
          
          <Dropdown
            menu={{
              items: [
                {
                  key: 'schema',
                  label: 'Download Schema (YAML)',
                  icon: <FileTextOutlined />,
                  onClick: () => handleDownloadSchema(record)
                },
                {
                  key: 'jsonl',
                  label: 'Download Training Data (JSONL)',
                  icon: <DownloadOutlined />,
                  onClick: () => handleDownload(record.id, record.name)
                }
              ]
            }}
          >
            <Button icon={<DownloadOutlined />} size="small">
              <span style={{ marginLeft: '4px' }}>Download</span>
            </Button>
          </Dropdown>
          
          {record.status === 'draft' && (
            <Tooltip title="Complete Dataset">
              <Button
                type="primary"
                size="small"
                onClick={() => handleCompleteDataset(record)}
              >
                Complete
              </Button>
            </Tooltip>
          )}
          
          <Tooltip title="Delete">
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              onClick={() => handleDelete(record.id, record.name)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <DatabaseOutlined /> Training Datasets
        </Title>
        <Text type="secondary">
          Manage your training datasets - generated from databases or uploaded schemas
        </Text>
      </div>

      {/* Actions Bar */}
      <Card style={{ marginBottom: '24px' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space wrap>
            <Search
              placeholder="Search datasets..."
              allowClear
              style={{ width: 300 }}
              onSearch={setSearchTerm}
              enterButton={<SearchOutlined />}
            />
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 150 }}
              suffixIcon={<FilterOutlined />}
            >
              <Option value="all">All Status</Option>
              <Option value="draft">Draft</Option>
              <Option value="confirmed">Confirmed</Option>
              <Option value="training">Training</Option>
            </Select>
            <Select
              value={filterSource}
              onChange={setFilterSource}
              style={{ width: 180 }}
              suffixIcon={<FilterOutlined />}
            >
              <Option value="all">All Sources</Option>
              <Option value="schema">Database Schema</Option>
              <Option value="upload">Uploaded Schema</Option>
            </Select>
          </Space>

          <Space wrap>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/training/datasets/generate')}
            >
              Generate from Database
            </Button>
            <Button
              icon={<DatabaseOutlined />}
              onClick={() => navigate('/training/datasets/upload')}
            >
              Upload Schema
            </Button>
          </Space>
        </Space>
      </Card>

      {/* Datasets Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={datasets}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} datasets`,
          }}
        />
      </Card>

      {/* Schema Viewer Modal */}
      {selectedDataset && (
        <SchemaViewer
          datasetId={selectedDataset.id}
          datasetName={selectedDataset.name}
          visible={schemaViewerVisible}
          onClose={() => setSchemaViewerVisible(false)}
          onEdit={handleEditSchema}
        />
      )}

      {/* Schema Editor Modal */}
      {schemaToEdit && selectedDataset && (
        <SchemaEditor
          datasetId={selectedDataset.id}
          datasetName={selectedDataset.name}
          initialSchema={schemaToEdit}
          visible={schemaEditorVisible}
          onClose={handleEditorClose}
        />
      )}
    </div>
  )
}

export default DatasetListPage
